import { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Routes, Route, useParams } from "react-router-dom";

import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import RouteMeta from "@/components/RouteMeta";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import LocaleTextLayer from "@/components/LocaleTextLayer";
import Home from "@/pages/Home";
import SkipToContent from "@/components/SkipToContent";
import NotFound from "@/pages/NotFound";
import PreserveContextRedirect from "@/components/PreserveContextRedirect";
import RouteScroll from "@/components/RouteScroll";
import { CAMPAIGN_ALIASES } from "@/data/sitePages";

const Departments = lazy(() => import("@/pages/Departments"));
const Plataforma = lazy(() => import("@/pages/Plataforma"));
const Contato = lazy(() => import("@/pages/Contato"));
const CasosDeUso = lazy(() => import("@/pages/CasosDeUso"));
const Solucoes = lazy(() => import("@/pages/Solucoes"));
const Precos = lazy(() => import("@/pages/Precos"));
const Empresa = lazy(() => import("@/pages/Empresa"));
const Docs = lazy(() => import("@/pages/Docs"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Studies = lazy(() => import("@/pages/Studies"));
const AiEconomics = lazy(() => import("@/pages/AiEconomics"));
const Journeys = lazy(() => import("@/pages/Journeys"));
const AiCoe = lazy(() => import("@/pages/AiCoe"));
const StudyDetail = lazy(() => import("@/pages/StudyDetail"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const SitePage = lazy(() => import("@/pages/SitePage"));
const Support = lazy(() => import("@/pages/Support"));
const SafeAiGuide = lazy(() => import("@/pages/SafeAiGuide"));

const RouteFallback = () => (
    <main style={{ minHeight: "58vh", background: "var(--zild-warm-paper)" }} aria-label="Carregando" />
);

const lazyRoute = (element) => (
    <Suspense fallback={<RouteFallback />}>
        {element}
    </Suspense>
);

function App() {
    const campaignRoutes = Object.entries(CAMPAIGN_ALIASES).flatMap(([path, pageKey]) => [
        <Route key={path} path={`/${path}`} element={lazyRoute(<SitePage pageKey={pageKey} />)} />,
        <Route key={`pt-${path}`} path={`/pt-BR/${path}`} element={lazyRoute(<SitePage pageKey={pageKey} />)} />,
        <Route key={`en-${path}`} path={`/en-US/${path}`} element={lazyRoute(<SitePage pageKey={pageKey} />)} />,
    ]);

    return (
        <div className="App">
            <BrowserRouter>
                <SkipToContent />
                <RouteScroll />
                <AnalyticsTracker />
                <SiteHeader />
                <LocaleTextLayer />
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/pt-BR" element={<Home />} />
                    <Route path="/en-US" element={<Home />} />
                    <Route path="/plataforma" element={lazyRoute(<Plataforma />)} />
                    <Route path="/pt-BR/platform" element={lazyRoute(<Plataforma />)} />
                    <Route path="/en-US/platform" element={lazyRoute(<Plataforma />)} />
                    <Route path="/solucoes" element={lazyRoute(<Solucoes />)} />
                    <Route path="/pt-BR/solutions" element={lazyRoute(<Solucoes />)} />
                    <Route path="/en-US/solutions" element={lazyRoute(<Solucoes />)} />
                    <Route path="/pt-BR/solutions/:slug" element={<SitePageFromParam prefix="solutions" />} />
                    <Route path="/en-US/solutions/:slug" element={<SitePageFromParam prefix="solutions" />} />
                    <Route path="/solutions/:slug" element={<SitePageFromParam prefix="solutions" />} />
                    <Route path="/caso-de-uso" element={lazyRoute(<CasosDeUso />)} />
                    <Route path="/casos-de-uso" element={lazyRoute(<CasosDeUso />)} />
                    <Route path="/use-case" element={lazyRoute(<CasosDeUso />)} />
                    <Route path="/use-cases" element={lazyRoute(<CasosDeUso />)} />
                    <Route path="/pt-BR/caso-de-uso" element={lazyRoute(<CasosDeUso />)} />
                    <Route path="/pt-BR/casos-de-uso" element={lazyRoute(<CasosDeUso />)} />
                    <Route path="/pt-BR/use-case" element={lazyRoute(<CasosDeUso />)} />
                    <Route path="/pt-BR/use-cases" element={lazyRoute(<CasosDeUso />)} />
                    <Route path="/en-US/use-cases" element={lazyRoute(<CasosDeUso />)} />
                    <Route path="/pt-BR/products" element={<Navigate to="/pt-BR/platform" replace />} />
                    <Route path="/en-US/products" element={<Navigate to="/en-US/platform" replace />} />
                    <Route path="/products" element={<Navigate to="/pt-BR/platform" replace />} />
                    <Route path="/products/docs" element={<Navigate to="/pt-BR/docs/api-reference/documents-introduction" replace />} />
                    <Route path="/docs/apps/zild-docs/*" element={<Navigate to="/pt-BR/docs/api-reference/documents-introduction" replace />} />
                    <Route path="/pt-BR/products/docs" element={<Navigate to="/pt-BR/docs/api-reference/documents-introduction" replace />} />
                    <Route path="/pt-BR/docs/apps/zild-docs/*" element={<Navigate to="/pt-BR/docs/api-reference/documents-introduction" replace />} />
                    <Route path="/en-US/products/docs" element={<Navigate to="/en-US/docs/api-reference/documents-introduction" replace />} />
                    <Route path="/en-US/docs/apps/zild-docs/*" element={<Navigate to="/en-US/docs/api-reference/documents-introduction" replace />} />
                    <Route path="/products/tasks" element={<Navigate to="/pt-BR/products/flow" replace />} />
                    <Route path="/pt-BR/products/tasks" element={<Navigate to="/pt-BR/products/flow" replace />} />
                    <Route path="/en-US/products/tasks" element={<Navigate to="/en-US/products/flow" replace />} />
                    <Route path="/pt-BR/products/:slug" element={<SitePageFromParam prefix="products" />} />
                    <Route path="/en-US/products/:slug" element={<SitePageFromParam prefix="products" />} />
                    <Route path="/products/:slug" element={<SitePageFromParam prefix="products" />} />
                    <Route path="/pt-BR/departments" element={lazyRoute(<Departments />)} />
                    <Route path="/en-US/departments" element={lazyRoute(<Departments />)} />
                    <Route path="/departments" element={lazyRoute(<Departments />)} />
                    <Route path="/pt-BR/industries" element={lazyRoute(<SitePage pageKey="industries" />)} />
                    <Route path="/en-US/industries" element={lazyRoute(<SitePage pageKey="industries" />)} />
                    <Route path="/industries" element={lazyRoute(<SitePage pageKey="industries" />)} />
                    <Route path="/pt-BR/industries/:slug" element={<SitePageFromParam prefix="industries" />} />
                    <Route path="/en-US/industries/:slug" element={<SitePageFromParam prefix="industries" />} />
                    <Route path="/industries/:slug" element={<SitePageFromParam prefix="industries" />} />
                    <Route path="/pt-BR/implementation" element={lazyRoute(<SitePage pageKey="implementation" />)} />
                    <Route path="/en-US/implementation" element={lazyRoute(<SitePage pageKey="implementation" />)} />
                    <Route path="/implementation" element={lazyRoute(<SitePage pageKey="implementation" />)} />
                    <Route path="/precos" element={lazyRoute(<Precos />)} />
                    <Route path="/pt-BR/precos" element={lazyRoute(<Precos />)} />
                    <Route path="/pt-BR/pricing" element={lazyRoute(<Precos />)} />
                    <Route path="/en-US/pricing" element={lazyRoute(<Precos />)} />
                    <Route path="/en-US/precos" element={<PreserveContextRedirect to="/en-US/pricing" />} />
                    <Route path="/about" element={<Navigate to="/pt-BR/about" replace />} />
                    <Route path="/pt-BR/about" element={lazyRoute(<Empresa />)} />
                    <Route path="/en-US/about" element={lazyRoute(<Empresa />)} />
                    <Route path="/empresa" element={<Navigate to="/pt-BR/about" replace />} />
                    <Route path="/company" element={<Navigate to="/pt-BR/about" replace />} />
                    <Route path="/pt-BR/company" element={<Navigate to="/pt-BR/about" replace />} />
                    <Route path="/pt-BR/empresa" element={<Navigate to="/pt-BR/about" replace />} />
                    <Route path="/en-US/company" element={<Navigate to="/en-US/about" replace />} />
                    <Route path="/docs/*" element={lazyRoute(<Docs />)} />
                    <Route path="/pt-BR/docs/*" element={lazyRoute(<Docs />)} />
                    <Route path="/en-US/docs/*" element={lazyRoute(<Docs />)} />
                    <Route path="/blog" element={lazyRoute(<Blog />)} />
                    <Route path="/pt-BR/blog" element={lazyRoute(<Blog />)} />
                    <Route path="/en-US/blog" element={lazyRoute(<Blog />)} />
                    <Route path="/blog/:slug" element={lazyRoute(<BlogPost />)} />
                    <Route path="/pt-BR/blog/:slug" element={lazyRoute(<BlogPost />)} />
                    <Route path="/en-US/blog/:slug" element={lazyRoute(<BlogPost />)} />
                    <Route path="/diagnostico" element={<Navigate to="/contato?source=legacy_diagnostic" replace />} />
                    <Route path="/pt-BR/diagnostico" element={<Navigate to="/pt-BR/contato?source=legacy_diagnostic" replace />} />
                    <Route path="/en-US/diagnostico" element={<Navigate to="/en-US/contact?source=legacy_diagnostic" replace />} />
                    <Route path="/cases" element={<PreserveContextRedirect to="/pt-BR/journeys" />} />
                    <Route path="/pt-BR/cases" element={<PreserveContextRedirect to="/pt-BR/journeys" />} />
                    <Route path="/en-US/cases" element={<PreserveContextRedirect to="/en-US/journeys" />} />
                    <Route path="/estudos" element={lazyRoute(<Studies />)} />
                    <Route path="/pt-BR/journeys" element={lazyRoute(<Journeys />)} />
                    <Route path="/pt-BR/journeys/ai-coe" element={lazyRoute(<AiCoe />)} />
                    <Route path="/en-US/journeys/ai-coe" element={lazyRoute(<AiCoe />)} />
                    <Route path="/en-US/journeys" element={lazyRoute(<Journeys />)} />
                    <Route path="/pt-BR/tools/ai-economics" element={lazyRoute(<AiEconomics />)} />
                    <Route path="/en-US/tools/ai-economics" element={lazyRoute(<AiEconomics />)} />
                    <Route path="/pt-BR/studies" element={lazyRoute(<Studies />)} />
                    <Route path="/en-US/studies" element={lazyRoute(<Studies />)} />
                    <Route path="/estudos/:slug" element={lazyRoute(<StudyDetail />)} />
                    <Route path="/pt-BR/studies/:slug" element={lazyRoute(<StudyDetail />)} />
                    <Route path="/en-US/studies/:slug" element={lazyRoute(<StudyDetail />)} />
                    <Route path="/guia-de-uso-seguro-de-ia" element={lazyRoute(<SafeAiGuide />)} />
                    <Route path="/pt-BR/guia-de-uso-seguro-de-ia" element={lazyRoute(<SafeAiGuide />)} />
                    <Route path="/en-US/guia-de-uso-seguro-de-ia" element={lazyRoute(<SafeAiGuide />)} />
                    <Route path="/support" element={lazyRoute(<Support />)} />
                    <Route path="/pt-BR/support" element={lazyRoute(<Support />)} />
                    <Route path="/en-US/support" element={lazyRoute(<Support />)} />
                    <Route path="/privacy" element={lazyRoute(<LegalPage type="privacy" />)} />
                    <Route path="/pt-BR/privacy" element={lazyRoute(<LegalPage type="privacy" />)} />
                    <Route path="/en-US/privacy" element={lazyRoute(<LegalPage type="privacy" />)} />
                    <Route path="/terms" element={lazyRoute(<LegalPage type="terms" />)} />
                    <Route path="/pt-BR/terms" element={lazyRoute(<LegalPage type="terms" />)} />
                    <Route path="/en-US/terms" element={lazyRoute(<LegalPage type="terms" />)} />
                    <Route path="/login" element={<ExternalRedirect />} />
                    <Route path="/pt-BR/login" element={<ExternalRedirect />} />
                    <Route path="/en-US/login" element={<ExternalRedirect />} />
                    <Route path="/contato" element={lazyRoute(<Contato />)} />
                    <Route path="/pt-BR/contact" element={lazyRoute(<Contato />)} />
                    <Route path="/pt-BR/contato" element={lazyRoute(<Contato />)} />
                    <Route path="/en-US/contact" element={lazyRoute(<Contato />)} />
                    <Route path="/en-US/contato" element={<PreserveContextRedirect to="/en-US/contact" />} />
                    {campaignRoutes}
                    <Route path="*" element={<NotFound />} />
                </Routes>
                <RouteMeta />
                <SiteFooter />
            </BrowserRouter>
        </div>
    );
}

const SitePageFromParam = ({ prefix }) => {
    const { slug } = useParams();
    return lazyRoute(<SitePage pageKey={`${prefix}/${slug}`} />);
};

const ExternalRedirect = () => {
    useEffect(() => {
        window.location.href = "https://app.zild.ai/";
    }, []);

    return (
        <main style={{ minHeight: "58vh", display: "grid", placeItems: "center" }}>
            <a className="zild-btn zild-btn-primary" href="https://app.zild.ai/">
                Continuar para login
            </a>
        </main>
    );
};

export default App;
